import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatInr } from "@/lib/api";
import { Trash2, Minus, Plus } from "lucide-react";

export default function Cart() {
  const { items, updateQty, remove, subtotal, gst, shipping, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="overline">Your cart</div>
        <h1 className="font-display text-4xl mt-3">No items yet.</h1>
        <p className="text-[var(--fm-text-2)] mt-3">Browse our catalogue to add equipment, instruments and accessories.</p>
        <Link to="/products" className="fm-btn-primary mt-8 inline-block" data-testid="empty-cart-cta">Browse catalogue</Link>
      </div>
    );
  }

  const goCheckout = () => {
    if (!user) navigate("/login?next=/checkout"); else navigate("/checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-8">
        <div className="overline">Shopping cart</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Your selection</h1>

        <div className="mt-8 divide-y divide-[var(--fm-border)] border-y border-[var(--fm-border)]">
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-5 py-6">
              <Link to={`/products/${i.slug}`} className="w-28 h-28 bg-[var(--fm-sage)] overflow-hidden flex-shrink-0">
                <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1">
                <Link to={`/products/${i.slug}`} className="font-display text-lg hover:text-[var(--fm-primary)]">{i.name}</Link>
                <div className="font-mono-fm text-sm text-[var(--fm-text-2)] mt-1">{formatInr(i.price)}</div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center border border-[var(--fm-border)]">
                    <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-2"><Minus size={12} /></button>
                    <span className="px-4 text-sm font-mono-fm">{i.quantity}</span>
                    <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-2"><Plus size={12} /></button>
                  </div>
                  <button onClick={() => remove(i.product_id)} data-testid={`cart-remove-${i.product_id}`} className="text-xs text-[var(--fm-text-3)] hover:text-[var(--fm-primary)] inline-flex items-center gap-1">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
              <div className="font-mono-fm text-base font-semibold">{formatInr(i.price * i.quantity)}</div>
            </div>
          ))}
        </div>
      </div>

      <aside className="lg:col-span-4">
        <div className="bg-white border border-[var(--fm-border)] p-7 lg:sticky lg:top-28">
          <div className="overline">Summary</div>
          <div className="mt-5 space-y-3 text-sm">
            <Row label="Subtotal" value={formatInr(subtotal)} />
            <Row label="GST (18%)" value={formatInr(gst)} />
            <Row label="Shipping" value={shipping === 0 ? "Free" : formatInr(shipping)} />
            <div className="border-t border-[var(--fm-border)] pt-3 mt-3 flex justify-between text-base">
              <span className="font-medium">Total</span>
              <span className="font-mono-fm font-semibold text-[var(--fm-primary)]" data-testid="cart-total">{formatInr(total)}</span>
            </div>
          </div>
          <button onClick={goCheckout} data-testid="checkout-btn" className="fm-btn-primary w-full mt-6">Proceed to checkout</button>
          <Link to="/contact" className="fm-btn-secondary w-full mt-3 block text-center">Convert cart to enquiry</Link>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[var(--fm-text-2)]">
      <span>{label}</span>
      <span className="font-mono-fm">{value}</span>
    </div>
  );
}
